import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";

const T = {
  en: { h: "How did we do?", p: "3 quick questions about the grant workshop", q1: "How helpful was the workshop?", q2: "How likely are you to recommend it to a friend?", q3: "What should we cover next?", ph: "e.g. writing a budget, SBA loans, grants for nonprofits", lo: ["Not at all", "Very"], btn: "Send feedback", done: "Thank you! Your feedback helps us plan the next workshop.", need: "Please answer the first two questions." },
  es: { h: "¿Cómo nos fue?", p: "3 preguntas rápidas sobre el taller de subvenciones", q1: "¿Qué tan útil fue el taller?", q2: "¿Qué tan probable es que lo recomiende a un amigo?", q3: "¿Qué tema deberíamos cubrir después?", ph: "p. ej. hacer un presupuesto, préstamos SBA, subvenciones para organizaciones sin fines de lucro", lo: ["Nada", "Mucho"], btn: "Enviar opinión", done: "¡Gracias! Su opinión nos ayuda a planear el próximo taller.", need: "Responda las dos primeras preguntas." },
};

function Scale({ value, onChange, id, lo }) {
  return (
    <div>
      <div className="grid grid-cols-5 gap-2" role="radiogroup">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => onChange(n)} role="radio" aria-checked={value === n} data-testid={`fb-${id}-${n}`}
            className={`h-12 rounded-md border text-base font-semibold transition-colors ${value === n ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500 mt-1"><span>{lo[0]}</span><span>{lo[1]}</span></div>
    </div>
  );
}

export default function WorkshopFeedback() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [w, setW] = useState(null);
  const [f, setF] = useState({ helpful: 0, recommend: 0, next_topic: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/workshops/${id}`).then((r) => setW(r.data)).catch(() => setErr("Workshop not found")); }, [id]);
  const L = T[w?.lang || "en"];
  const send = async () => {
    if (!f.helpful || !f.recommend) { setErr(L.need); return; }
    setBusy(true); setErr("");
    try { await api.post(`/public/workshops/${id}/feedback`, { ...f, k: params.get("k") || "" }); setDone(true); }
    catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50" data-testid="workshop-feedback-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-xl mx-auto px-4 py-4"><Brand /></div></header>
      <main className="max-w-xl mx-auto px-4 py-10">
        <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
          {done ? <p className="flex items-start gap-2 text-emerald-800 font-semibold" data-testid="fb-done"><CheckCircle2 className="h-5 w-5 shrink-0" />{L.done}</p> : (
            <>
              <h1 className="font-display text-3xl font-extrabold text-slate-900">{L.h}</h1>
              <p className="mt-1 text-slate-600">{L.p}{w ? ` · ${w.library}, ${w.display_date}` : ""}</p>
              <div className="mt-8 space-y-7">
                <div><p className="font-semibold text-slate-900 mb-2">1. {L.q1}</p><Scale value={f.helpful} onChange={(v) => { setF({ ...f, helpful: v }); setErr(""); }} id="helpful" lo={L.lo} /></div>
                <div><p className="font-semibold text-slate-900 mb-2">2. {L.q2}</p><Scale value={f.recommend} onChange={(v) => { setF({ ...f, recommend: v }); setErr(""); }} id="recommend" lo={L.lo} /></div>
                <div><p className="font-semibold text-slate-900 mb-2">3. {L.q3}</p><Textarea value={f.next_topic} onChange={(e) => setF({ ...f, next_topic: e.target.value })} placeholder={L.ph} maxLength={600} data-testid="fb-next" /></div>
              </div>
              {err && <p className="mt-4 text-sm text-red-600" data-testid="fb-error">{err}</p>}
              <Button onClick={send} disabled={busy || !w} className="mt-6 w-full h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="fb-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{L.btn}</Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
